import React, { useEffect, useState } from 'react';
import { PortalShell } from '@/components/nav/PortalShell';
import { portalApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Home as HomeIcon, Calendar, RefreshCw, ExternalLink } from 'lucide-react';
import { fmtDate } from '@/lib/format';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { PORTAL } from '@/constants/testIds';

export default function PortalPropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [gcalDrafts, setGcalDrafts] = useState({});

  const load = () => portalApi.properties()
    .then(list => { setProperties(list); const drafts = {}; list.forEach(p => drafts[p.id] = p.google_calendar_ical_url || ''); setGcalDrafts(drafts); })
    .catch(() => {}) // surfaced by the api response interceptor
    .finally(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const saveGcal = async (pid) => {
    try {
      await portalApi.setGoogleCalendar(pid, gcalDrafts[pid]);
      toast.success('Google Calendar link saved');
      load();
    } catch { toast.error('Failed'); }
  };
  const sync = async (pid) => {
    try {
      const r = await portalApi.syncProperty(pid);
      if (r.skipped) { toast('No iCal URL configured', { icon: 'ℹ️' }); return; }
      toast.success(`Synced: ${r.jobs_created || 0} created, ${r.jobs_updated || 0} updated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
  };

  return (
    <PortalShell title="Properties">
      <h1 className="font-display text-2xl md:text-3xl font-semibold mb-1">Properties</h1>
      <p className="text-sm text-muted-foreground mb-4">Link your Google Calendar so we can automatically schedule cleanings between guests.</p>
      {!loaded ? (
        <ListSkeleton rows={3} />
      ) : properties.length === 0 ? (
        <EmptyState icon={HomeIcon} title="No properties yet" description="Once we add your properties they will appear here." />
      ) : (
        <div className="space-y-3">
          {properties.map(p => (
            <Card key={p.id}>
              <CardHeader className="flex flex-row items-start justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><HomeIcon className="h-4 w-4 text-primary" />{p.nickname}</CardTitle>
                  <div className="text-xs text-muted-foreground mt-1">{p.address || '—'} · {p.bedrooms} bd · {p.bathrooms} ba</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => sync(p.id)}><RefreshCw className="h-3 w-3 mr-1" />Sync now</Button>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Airbnb/VRBO iCal:</span>{' '}
                    {p.ical_url ? <span className="font-mono text-[11px] break-all">{p.ical_url}</span> : <span className="text-muted-foreground">not configured (contact us to add)</span>}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last sync:</span> {p.last_ical_sync ? fmtDate(p.last_ical_sync) : 'never'}
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="text-sm font-medium mb-1 flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" />Google Calendar iCal URL</div>
                  <div className="text-xs text-muted-foreground mb-2">
                    In Google Calendar → Settings → your calendar → Integrate calendar → copy the <b>Secret address in iCal format</b>, then paste it here.
                    {' '}
                    <a href="https://support.google.com/calendar/answer/37648?hl=en" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">Google guide <ExternalLink className="h-3 w-3" /></a>
                  </div>
                  <div className="flex gap-2">
                    <Input placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
                      value={gcalDrafts[p.id] || ''}
                      onChange={e => setGcalDrafts({ ...gcalDrafts, [p.id]: e.target.value })}
                      data-testid={PORTAL.googleCalUrl}
                    />
                    <Button onClick={() => saveGcal(p.id)} data-testid={PORTAL.googleCalSave}>Save</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PortalShell>
  );
}
